// Figma frame 17 · Install (33:775) — US-130.
//
// SPEC NOTE, kept here rather than silently "fixed" (same discipline
// `screens/commerce/CheckoutSuccessScreen.tsx` documents for frame 20):
// the Figma frame draws a "YOUR LICENSE KEY" reveal showing a live plaintext
// key, immediately after issuance. Nowhere in this build does a session-
// authenticated route ever hand a browser a plaintext key — `POST
// /admin/licenses` (the only mint endpoint) is dev-token gated, not
// session-gated, and `GET /licenses` deliberately never returns one (Wave 1
// handoff, verified against `backend/api/routes/licenses_admin.py`). Drawing
// the reveal card here anyway would assert a key this build cannot back —
// the same reasoning the commerce screen already applied to frame 20's own
// masked key field. The ONE place a real plaintext key legitimately reaches
// this frontend is `just dev`'s local-only `VITE_MAISIGHT_KEY` (mirrors
// `dashboard/api.ts::getKey()`'s `import.meta.env.DEV` guard, which Vite
// strips from a production bundle entirely) — when present, the reveal
// renders for real; otherwise the snippet uses an explicit placeholder and
// the copy points at Support, never at a "rotate" call this build cannot
// make either.
//
// The Figma snippet also omits `data-maisight-org` — `widget/entry.ts
// ::readOpts` returns `null` (no mount at all) without both `doc` AND `org`.
// Verified against that file, not the mockup, per the story's own
// instruction. The `src` path is corrected too: `docs/monitoring/README.md`'s
// 2026-08-15 live check found `embed.maisight.codeblackwell.ai/v1.iife.js`
// 404s and only `…/static/embed/v1.iife.js` returns 200 — a customer pasting
// a 404ing script tag is a worse outcome than a pixel-exact mockup, and
// pixel convergence is explicitly the separate /crg-ui run's job, not this
// screen's.

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Icon,
  PageHeader,
  Skeleton,
} from "../../ui";
import { hrefs } from "../../shell/hrefs";
import { CopyField } from "./CopyField";
import { ArrivalsChart } from "./ArrivalsChart";
import { useArrivals, useInstallCheck, useLicense } from "./api";
import "./ops.css";

const EMBED_SRC = "https://embed.maisight.codeblackwell.ai/static/embed/v1.iife.js";
const KEY_PLACEHOLDER = "YOUR_LICENSE_KEY";
const DOC_PLACEHOLDER = "YOUR_DOC_ID";

/** The window the verify check reports over. Named in every sentence it
 *  produces — "no traffic in the last 24 hours" is checkable, "not live" is
 *  not. Short on purpose: this screen is read minutes after a deploy. */
const VERIFY_WINDOW_HOURS = 24;

// A week of arrivals under the banner. Longer than the verify window on
// purpose: the banner answers "is it live right now", the chart answers
// "has it been", and a 24-hour chart of a healthy install is one bar.
const ARRIVALS_WINDOW_HOURS = 168;

/** `just dev`'s local-only channel — the same guard `dashboard/api.ts
 *  ::getKey()` uses, so a real key can never ship in a production bundle. */
function devIssuedKey(): string | null {
  return import.meta.env.DEV
    ? ((import.meta.env?.VITE_MAISIGHT_KEY as string | undefined) ?? null)
    : null;
}

/** Exported for `InstallScreen.test.tsx` — this is the exact string a
 *  customer pastes, and `widget/entry.ts::readOpts` requires BOTH `doc` AND
 *  `org` or it mounts nothing at all, so the shape is worth pinning. */
export function buildSnippet(orgSlug: string, key: string): string {
  return [
    `<script src="${EMBED_SRC}"`,
    `        data-maisight-key="${key}"`,
    `        data-maisight-org="${orgSlug}"`,
    `        data-maisight-doc="${DOC_PLACEHOLDER}"`,
    `        defer></script>`,
  ].join("\n");
}

function planLabel(plan: string | null): string {
  if (!plan) return "No plan on file";
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

function statusTone(state: string): "nominal" | "flagged" | "danger" | "neutral" {
  if (state === "active") return "nominal";
  if (state === "trial") return "flagged";
  if (state === "expired") return "danger";
  return "neutral";
}

export function InstallScreen({ orgSlug }: { orgSlug: string }) {
  const navigate = useNavigate();
  const license = useLicense();
  const install = useInstallCheck(VERIFY_WINDOW_HOURS);
  const arrivals = useArrivals(ARRIVALS_WINDOW_HOURS);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  const key = devIssuedKey();
  const snippet = buildSnippet(orgSlug, key ?? KEY_PLACEHOLDER);

  // `reporting` is the ONLY verdict that confirms anything. The other two are
  // traffic statements, so they get the neutral tone — never `danger`, which
  // is what "your install is broken" looks like on this chassis.
  const reporting = install.data?.verdict === "reporting";

  return (
    <>
      <PageHeader
        eyebrow="Install"
        title="Add mAiSight to your docs"
        description="One script tag. It reads your published glossary and highlights terms in place."
      />

      <div className="maisight-ops-body">
        <div
          className="maisight-ops-banner"
          data-chassis="paper"
          data-tone={install.isError ? "danger" : reporting ? "nominal" : "neutral"}
        >
          <Icon
            name={install.isError ? "alert-triangle" : reporting ? "check" : "clock"}
            size={18}
          />
          <div className="maisight-ops-banner-body">
            <p className="type-bodyS" data-testid="verify-result">
              {install.isPending
                ? "Checking for reader activity…"
                : install.isError
                  ? "We couldn't reach the check just now, so we don't know either way. Try again in a moment."
                  : /* The backend owns the sentence — the query behind it and
                       the claim it makes cannot drift apart in a renderer. */
                    install.data?.detail}
            </p>
            {/* Shown only when we have not seen traffic: that is exactly when a
                customer starts guessing at a cause, and the honest answer is
                that we cannot see their domain at all. */}
            {install.data && !reporting ? (
              <p
                className="type-labelSm maisight-ops-card-note"
                data-testid="verify-domain-note"
              >
                {install.data.domain_check_reason}
              </p>
            ) : null}
          </div>
          <Button
            variant="ghost"
            size="sm"
            data-testid="verify-install"
            onClick={() => {
              void install.refetch();
              void license.refetch();
            }}
          >
            Verify it is live
          </Button>
        </div>

        {/* The one full-width slot in the body flow. `ArrivalsChart`
         *  renders nothing at all when nothing has arrived — an absence
         *  is prose here, never a series of zeros. */}
        {arrivals.data ? <ArrivalsChart series={arrivals.data} /> : null}

        <div className="maisight-ops-columns" data-chassis="paper">
          <div className="maisight-ops-col-left">
            <Card header="Embed snippet">
              {key ? (
                <>
                  <p className="type-bodyS maisight-ops-card-note">
                    Shown once because it was just issued to this dev session. We
                    store only a hash of it, so we can't show it to you again —
                    copy it now.
                  </p>
                  <CopyField
                    value={key}
                    label="Your license key"
                    onCopied={() => setCopiedKey(true)}
                  />
                  {copiedKey ? (
                    <p className="type-bodyS maisight-ops-card-note">Copied.</p>
                  ) : null}
                </>
              ) : (
                <p className="type-bodyS maisight-ops-card-note">
                  Your license key is shown once, at issuance, and we only ever
                  store its hash — we can't display it again from here. If you
                  don't have it, ask an admin to reissue one via support.
                </p>
              )}
              <CopyField
                value={snippet}
                label="Embed snippet"
                block
                onCopied={() => setCopiedSnippet(true)}
              />
              {copiedSnippet ? (
                <p className="type-bodyS maisight-ops-card-note">Snippet copied.</p>
              ) : null}
              <div className="maisight-ops-steps">
                <div className="maisight-ops-step">
                  <span className="maisight-ops-step-badge" data-state="done">
                    <Icon name="check" size={14} />
                  </span>
                  <p className="type-bodyS">Paste the snippet into your docs template</p>
                </div>
                <div className="maisight-ops-step">
                  <span className="maisight-ops-step-badge" data-state="active">
                    2
                  </span>
                  <p className="type-bodyS">Deploy your docs site</p>
                </div>
                <div className="maisight-ops-step" data-state="pending">
                  <span className="maisight-ops-step-badge" data-state="pending">
                    3
                  </span>
                  <p className="type-bodyS">Terms start highlighting on the next page view</p>
                </div>
              </div>
            </Card>
          </div>

          <div className="maisight-ops-col-right">
            <Card header="License">
              {license.data ? (
                <>
                  <div className="maisight-ops-kv">
                    <span className="type-labelSm maisight-ops-kv-label">Plan</span>
                    <span className="type-bodyS maisight-ops-kv-value">
                      {planLabel(license.data.plan)}
                    </span>
                  </div>
                  <div className="maisight-ops-kv">
                    <span className="type-labelSm maisight-ops-kv-label">Status</span>
                    <Badge tone={statusTone(license.data.resolved_state)} lamp>
                      {license.data.resolved_state}
                    </Badge>
                  </div>
                  <div className="maisight-ops-kv">
                    <span className="type-labelSm maisight-ops-kv-label">Bound domain</span>
                    <span className="type-bodyS maisight-ops-kv-value">
                      {license.data.domains.length ? license.data.domains.join(", ") : "None yet"}
                    </span>
                  </div>
                  <div className="maisight-ops-kv">
                    <span className="type-labelSm maisight-ops-kv-label">
                      {license.data.resolved_state === "trial" ? "Trial ends" : "Expires"}
                    </span>
                    <span className="type-bodyS maisight-ops-kv-value">
                      {license.data.expires_at ?? "Never"}
                    </span>
                  </div>
                  <p className="type-bodyS maisight-ops-card-note">
                    Your key is stored as a hash — we can't show it again from
                    here.
                  </p>
                </>
              ) : license.isError ? (
                <ErrorState
                  headline="Couldn't load your license"
                  detail={license.error?.message}
                  action={
                    <Button variant="secondary" size="sm" onClick={() => void license.refetch()}>
                      Try again
                    </Button>
                  }
                />
              ) : (
                <>
                  <Skeleton width={280} height={16} />
                  <Skeleton width={220} height={16} />
                  <Skeleton width={240} height={16} />
                </>
              )}
            </Card>
            <Button
              variant="secondary"
              size="sm"
              block
              onClick={() => void navigate({ to: hrefs(orgSlug).support })}
            >
              Contact support
            </Button>
          </div>
        </div>

        <Card header="Activity">
          <div className="maisight-ops-activity">
            <div className="maisight-ops-activity-row">
              <span className="maisight-ops-activity-dot" />
              <div>
                <p className="type-bodyS maisight-ops-activity-title">License created</p>
                <p className="type-labelSm maisight-ops-activity-meta">
                  {license.data?.created_at ?? "Unknown"}
                </p>
              </div>
            </div>
            <div className="maisight-ops-activity-row">
              <span
                className="maisight-ops-activity-dot"
                data-tone={reporting ? undefined : "pending"}
              />
              <div>
                <p className="type-bodyS maisight-ops-activity-title">
                  {install.data?.last_event_at
                    ? "Last event received"
                    : "Waiting for the first reader event"}
                </p>
                <p className="type-labelSm maisight-ops-activity-meta">
                  {/* A timestamp or nothing. `never_reported` has no last-seen
                      to render, and a dash standing in for one would read as a
                      measured absence rather than an absent measurement. */}
                  {install.data?.last_event_at ??
                    "Updates automatically once your snippet is live."}
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
